<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { fade } from 'svelte/transition';

	import { flyAndScale } from '$lib/utils/transitions';
	import { openFloatingLayers } from '$lib/components/common/floating-layer';
	import * as FocusTrap from 'focus-trap';
	export let show = true;
	export let size: 'full' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' = 'md';
	export let containerClassName = 'p-3';
	export let className = 'bg-white dark:bg-gray-900 rounded-4xl';

	let modalElement: HTMLDivElement | null = null;
	let mounted = false;
	// Create focus trap to trap user tabs inside modal
	// https://www.w3.org/WAI/WCAG21/Understanding/focus-order.html
	// https://www.w3.org/WAI/WCAG21/Understanding/keyboard.html
	let focusTrap: FocusTrap.FocusTrap | null = null;

	const sizeToWidth = (size: string): string => {
		if (size === 'full') {
			return 'w-full';
		}
		if (size === 'xs') {
			return 'w-[16rem]';
		} else if (size === 'sm') {
			return 'w-[30rem]';
		} else if (size === 'md') {
			return 'w-[42rem]';
		} else if (size === 'lg') {
			return 'w-[56rem]';
		} else if (size === 'xl') {
			return 'w-[70rem]';
		} else if (size === '2xl') {
			return 'w-[84rem]';
		} else if (size === '3xl') {
			return 'w-[100rem]';
		} else {
			return 'w-[56rem]';
		}
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		// Escape belongs to an open portaled layer (Bits UI select menu, calendar popover) when one is
		// open: it closes itself, and closing the dialog too would throw away everything typed into it.
		// The layer count still reads its old value here — the DOM that clears it is torn down in a
		// later task — so this is the wrong place to ask `defaultPrevented` or the event target.
		if (floatingLayers > 0) return;

		if (event.key === 'Escape' && isTopModal()) {
			show = false;
		}
	};

	const isTopModal = () => {
		const modals = document.getElementsByClassName('modal');
		return modals.length && modals[modals.length - 1] === modalElement;
	};

	onMount(() => {
		mounted = true;
	});

	let floatingLayers = 0;

	// These handlers are declared outside the reactive block below on purpose. Svelte records every
	// variable read inside a reactive statement as a dependency — reads inside nested function
	// bodies included — so a `floatingLayers` read in a handler defined inside the mount block
	// re-runs that block whenever a portaled menu opens. The re-run moves the modal node again
	// (`document.body.appendChild`), which drops the scroll position of every scroll container
	// inside the dialog and blurs the focused control: clicking a select threw the panel back to
	// the top.
	function handleOutsidePointerDown(event: PointerEvent) {
		// Auto-pause focus trap when interacting with portaled content (e.g. Dropdown)
		if (
			focusTrap &&
			modalElement &&
			event.target instanceof Node &&
			!modalElement.contains(event.target)
		) {
			focusTrap.pause();
		}
	}

	function handleModalFocusIn() {
		// Never re-arm the trap while a portaled layer is open: that is what restarts the fight.
		if (focusTrap && floatingLayers === 0) {
			focusTrap.unpause();
		}
	}

	const unsubscribeFloatingLayers = openFloatingLayers.subscribe((count) => {
		floatingLayers = count;
	});

	// Component libraries portal floating layers (select menus, calendar popovers) to <body>, i.e.
	// outside this modal, and move focus into them when they open. With the trap active, focus-trap
	// and the library's own document listeners keep re-focusing each other until the call stack
	// overflows (an uncaught RangeError that breaks the control). Pausing the trap while such a layer
	// is open removes focus-trap's listeners entirely, which breaks that cycle.
	$: if (focusTrap) {
		if (floatingLayers > 0) focusTrap.pause();
		else focusTrap.unpause();
	}

	// Mount the trap exactly once per shown modal. Re-appending an attached node drops the scroll
	// position of every scroll container inside it, so the setup step must never run twice while the
	// trap exists.
	$: if (show && modalElement && !focusTrap) {
		document.body.appendChild(modalElement);
		focusTrap = FocusTrap.createFocusTrap(modalElement, {
			allowOutsideClick: (e) => {
				const target = e.target instanceof Element ? e.target : null;
				return (
					target?.closest('[data-sonner-toast]') !== null ||
					target?.closest('.modal-content') === null
				);
			}
		});
		focusTrap.activate();

		document.addEventListener('pointerdown', handleOutsidePointerDown, true);
		modalElement.addEventListener('focusin', handleModalFocusIn);

		window.addEventListener('keydown', handleKeyDown);
		document.body.style.overflow = 'hidden';
	} else if (!show && modalElement) {
		focusTrap?.deactivate();
		focusTrap = null;
		document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
		modalElement.removeEventListener('focusin', handleModalFocusIn);
		window.removeEventListener('keydown', handleKeyDown);
		if (modalElement.parentNode === document.body) document.body.removeChild(modalElement);
		document.body.style.overflow = 'unset';
	}

	onDestroy(() => {
		show = false;
		unsubscribeFloatingLayers();
		if (focusTrap) {
			focusTrap.deactivate();
		}
		if (modalElement && modalElement.parentNode === document.body) {
			document.body.removeChild(modalElement);
		}
	});
</script>

{#if show}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
	<div
		bind:this={modalElement}
		aria-modal="true"
		role="dialog"
		class="modal fixed top-0 right-0 left-0 bottom-0 bg-black/45 dark:bg-black/60 w-full h-screen max-h-[100dvh] {containerClassName}  flex justify-center z-9999 overflow-y-auto overscroll-contain"
		style="scrollbar-gutter: stable;"
		in:fade={{ duration: 10 }}
		on:mousedown={() => {
			show = false;
		}}
	>
		<div
			class="m-auto max-w-full {sizeToWidth(size)} {size !== 'full'
				? 'mx-2'
				: ''} shadow-3xl min-h-fit scrollbar-hidden {className} border border-white dark:border-gray-850"
			in:flyAndScale
			on:mousedown={(e) => {
				e.stopPropagation();
			}}
		>
			<slot />
		</div>
	</div>
{/if}

<style>
	.modal-content {
		animation: scaleUp 0.1s ease-out forwards;
	}

	@keyframes scaleUp {
		from {
			transform: scale(0.985);
			opacity: 0;
		}
		to {
			transform: scale(1);
			opacity: 1;
		}
	}
</style>
